import type { CandidateEvidenceSnapshot, CandidateFitObservation, HeightBand, LocalReview } from '@anjabom/core';

interface PublicCandidateEvidence {
  tierCounts: { exact: number; nearby: number; section: number };
  evidence?: readonly unknown[];
  otherConfiguration?: { count?: number };
  priceSampleCount?: number;
  priceDistribution?: CandidateEvidenceSnapshot['priceDistribution'];
  priceGrade?: string | null;
  priceDistributionReason?: CandidateEvidenceSnapshot['priceDistributionReason'];
}

/** One response owner for detail and comparison. Local tips have no original body profile. */
export function buildCandidateEvidenceSnapshot(publicEvidence: PublicCandidateEvidence | undefined, local: readonly LocalReview[]): CandidateEvidenceSnapshot {
  const fitObservations: CandidateFitObservation[] = [];
  let observedDimensions = 0;
  for (const value of publicEvidence?.evidence ?? []) {
    const entry = record(value);
    if (!entry) continue;
    const dimensions = record(entry.dimensions);
    observedDimensions += Object.values(dimensions ?? {}).filter((score) => validScore(score) !== null).length;
    if (entry.tier !== 'exact' && entry.tier !== 'nearby' && entry.tier !== 'section') continue;
    fitObservations.push({
      tier: entry.tier,
      contributorRef: typeof entry.contributorRef === 'string' ? entry.contributorRef : null,
      heightBand: heightBand(entry.heightBand),
      knee: validScore(dimensions?.knee),
      sound: validScore(dimensions?.sound),
      access: validScore(dimensions?.access),
    });
  }
  return {
    exact: publicEvidence?.tierCounts.exact ?? 0,
    nearby: publicEvidence?.tierCounts.nearby ?? 0,
    section: publicEvidence?.tierCounts.section ?? 0,
    otherConfiguration: publicEvidence?.otherConfiguration?.count ?? 0,
    observedDimensions,
    fitObservations,
    priceSampleCount: publicEvidence?.priceSampleCount ?? 0,
    priceDistribution: publicEvidence?.priceDistribution ?? null,
    priceGrade: publicEvidence?.priceGrade ?? null,
    priceDistributionReason: publicEvidence?.priceDistributionReason ?? 'no_recent_prices',
  };
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function validScore(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 3 ? value : null;
}

function heightBand(value: unknown): HeightBand | null {
  return value === 'u160' || value === '160-169' || value === '170-179' || value === '180-189' || value === 'o190' ? value : null;
}
